import { useEffect, useRef, useState } from "react";
import { ArrowRight, Phone, Star, Truck } from "lucide-react";
import { COMPANY, IMAGES, buildWhatsAppUrl } from "../../lib/constants";
import { WhatsAppIcon } from "./Header";
import { useInView } from "../../hooks/useInView";
import { useCountUp } from "../../hooks/useCountUp";

/**
 * HERO V5 — Real Photo 3D Collage with animated truck
 * - White background
 * - 4 real BRS photos arranged as tilted polaroid collage
 * - Mouse-parallax 3D tilt on photos
 * - Animated SVG truck driving across bottom
 * - Bold "WIR HOLEN ALLES AB." headline
 * - "Jetzt fahren" eye-catcher badge
 */
export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      id="top"
      className="relative bg-white text-black overflow-hidden pt-24 md:pt-28 pb-0"
    >
      <BgTexture />

      {/* Top editorial strip */}
      <div className="relative z-10 px-4 md:px-8 lg:px-12 flex items-center justify-between mb-6 md:mb-8 rise">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-black/70 border border-black/15 rounded-full px-3 py-1.5">
            N° 01 / 2026
          </span>
          <span className="hidden md:inline font-mono text-[10px] tracking-[0.4em] uppercase text-black/50">
            BREMEN · SEIT 2014
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#25D366] blink-dot" />
          <span className="font-mono text-[10px] tracking-[0.4em] uppercase text-black/65">
            LIVE · HEUTE FREI
          </span>
        </div>
      </div>

      {/* MAIN GRID */}
      <div className="relative z-10 px-4 md:px-8 lg:px-12 grid lg:grid-cols-12 gap-6 md:gap-8 lg:gap-10 items-center min-h-[560px] md:min-h-[760px]">
        {/* LEFT — TYPOGRAPHY */}
        <div className="lg:col-span-6 relative">
          <h1
            data-testid="hero-headline"
            className="font-display leading-[0.88] tracking-[-0.04em] font-semibold"
            style={{ fontSize: "clamp(40px, 11vw, 152px)" }}
          >
            <span
              className="block rise"
              style={{ animationDelay: "0.1s" }}
            >
              WIR HOLEN
            </span>
            <span
              className="block rise"
              style={{ animationDelay: "0.22s" }}
            >
              ALLES{" "}
              <span className="text-[#25D366] inline-block" style={{ WebkitTextStroke: "0.5px rgba(37,211,102,0.4)" }}>
                AB.
              </span>
            </span>
          </h1>

          {/* Service list with green dashes */}
          <div
            className="rise mt-5 md:mt-7 space-y-1.5 max-w-xl"
            style={{ animationDelay: "0.35s" }}
            data-testid="hero-service-list"
          >
            {[
              "Entrümpelung jeglicher Art",
              "Entsorgung jeglicher Art",
              "Kostenlose Schrottabholung jeglicher Art",
            ].map((line, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="block w-6 md:w-8 h-[2px] bg-[#25D366]" />
                <span className="text-[15px] md:text-[17px] font-medium text-black">
                  {line}
                </span>
              </div>
            ))}
          </div>

          {/* Customer-solution focused description */}
          <p
            className="rise mt-6 md:mt-8 max-w-xl text-[15px] md:text-[17px] text-black/70 leading-relaxed"
            style={{ animationDelay: "0.45s" }}
          >
            <span className="font-semibold text-black">1 Klick. 1 Foto. Erledigt.</span>{" "}
            Sie haben das Problem — wir die Lösung. Kein Stress, kein Aufwand,
            keine Vorauszahlung. Schreiben Sie uns und vergessen Sie den Rest.
          </p>

          {/* MASSIVE WhatsApp CTA (Bild 5 style) */}
          <div
            className="rise mt-7 md:mt-9 flex flex-col sm:flex-row gap-3"
            style={{ animationDelay: "0.55s" }}
          >
            <a
              href={buildWhatsAppUrl()}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-main"
              className="group relative inline-flex w-full sm:w-auto items-center gap-3 md:gap-4 px-5 md:px-6 py-3.5 md:py-4 rounded-2xl md:rounded-[20px] bg-[#25D366] text-black hover:scale-[1.015] transition-transform glow-pulse"
            >
              <span className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-white text-[#25D366] flex items-center justify-center shrink-0">
                <WhatsAppIcon className="w-5 h-5 md:w-6 md:h-6" />
              </span>
              <span className="flex flex-col items-start leading-tight">
                <span className="font-display text-base md:text-xl font-semibold tracking-tight">
                  Foto per WhatsApp senden
                </span>
                <span className="text-[11px] md:text-[12px] text-black/75 mt-0.5">
                  Schnell. Unkompliziert. Kostenlos.
                </span>
              </span>
              <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5 hidden sm:inline-block" />
            </a>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="hero-cta-call"
              className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 h-14 md:h-[68px] px-5 md:px-6 rounded-2xl md:rounded-[20px] border-2 border-black bg-white text-black text-sm font-semibold hover:bg-black hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4" />
              {COMPANY.phone}
            </a>
          </div>

          <div
            className="rise mt-8 md:mt-10 flex flex-wrap items-center gap-5 md:gap-8"
            style={{ animationDelay: "0.7s" }}
          >
            <a
              href={COMPANY.googleReviewUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2.5 group"
              data-testid="hero-google-badge"
            >
              <div className="flex gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    className="w-3.5 h-3.5 fill-black text-black"
                  />
                ))}
              </div>
              <span className="text-xs md:text-sm text-black/70 group-hover:text-black">
                <span className="font-medium text-black">4.9</span> · Google Reviews
              </span>
            </a>
            <div className="h-4 w-px bg-black/15 hidden md:block" />
            <div className="text-xs md:text-sm text-black/55">
              {COMPANY.area}
            </div>
          </div>
        </div>

        {/* RIGHT — Hero photo, single big card */}
        <div className="lg:col-span-6 relative h-[300px] sm:h-[400px] md:h-[520px] lg:h-[600px]">
          <HeroPhoto />
        </div>
      </div>

      {/* BIG GREEN "JETZT FAHREN" BANNER */}
      <NowDrivingBanner />

      {/* ANIMATED TRUCK STRIP */}
      <TruckStrip />

      {/* STATS */}
      <div className="relative z-10 mt-8 md:mt-12 px-4 md:px-8 lg:px-12">
        <div className="h-px bg-gradient-to-r from-transparent via-black/20 to-transparent mb-7 md:mb-10" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          <CountStat label="Aufträge / Monat" target={240} suffix="+" />
          <CountStat label="Anfahrt Schrott" target={0} suffix=" €" />
          <CountStat label="Antwortzeit" target={24} prefix="<" suffix=" h" />
          <CountStat label="Zufriedenheit" target={99} suffix=" %" />
        </div>
        <div className="h-px bg-gradient-to-r from-transparent via-black/20 to-transparent mt-7 md:mt-10" />
      </div>

      {/* Service marquee */}
      <div className="relative z-10 mt-8 md:mt-10 bg-black text-white overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-4 md:py-5">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-10 md:gap-14 px-6 shrink-0"
            >
              {[
                "Haushaltsauflösung",
                "Betriebsauflösung",
                "Entrümpelung",
                "Schrottabholung",
                "Winterdienst",
                "Transport",
                "Entsorgung",
                "Messie-Wohnung",
              ].map((t, idx) => (
                <span
                  key={`${i}-${idx}`}
                  className="text-xl md:text-3xl font-display font-medium tracking-tight flex items-center gap-10 md:gap-14"
                >
                  {t}
                  <span className="w-1.5 h-1.5 rounded-full bg-[#25D366]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* =========================================================
   HERO PHOTO — single clean photo card (replaces collage)
========================================================= */
const HeroPhoto = () => (
  <div className="relative w-full h-full" data-testid="hero-photo">
    {/* Soft glow */}
    <div
      className="absolute -inset-6 rounded-[40px] pointer-events-none opacity-50"
      style={{
        background:
          "radial-gradient(60% 50% at 70% 50%, rgba(37,211,102,0.15), transparent 70%)",
      }}
      aria-hidden
    />

    {/* Main photo */}
    <div className="relative w-full h-full rounded-[28px] md:rounded-[36px] overflow-hidden border border-black/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.35)] float-soft">
      <img
        src={IMAGES.team.vanSide}
        alt="Bremer Recycling Service Transporter im Einsatz"
        className="absolute inset-0 w-full h-full object-cover slow-zoom"
        loading="eager"
      />
      {/* Subtle bottom gradient */}
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

      {/* Live tag */}
      <div className="absolute top-4 left-4 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.3em] uppercase text-white bg-black/55 backdrop-blur-sm border border-white/15 rounded-full px-2.5 py-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[#25D366] blink-dot" />
        Live · Bremen
      </div>

      {/* Bottom-right transporter chip */}
      <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-black border border-black/10 shadow-sm">
        <Truck className="w-3.5 h-3.5" />
        <span className="text-[11px] font-semibold tracking-tight">
          2 Transporter
        </span>
      </div>

      {/* Caption */}
      <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-white/55">
            Bremer Recycling Service
          </div>
          <div className="mt-1 font-display text-lg md:text-2xl font-semibold text-white tracking-tight leading-tight">
            24/7 · Bremen &amp; Umzu
          </div>
        </div>
        <div className="w-10 h-10 rounded-full bg-[#25D366] text-black flex items-center justify-center shrink-0">
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </div>
  </div>
);

/* =========================================================
   PHOTO COLLAGE — mouse-parallax tilt + floating cards (legacy, unused)
========================================================= */
const PhotoCollage3D = () => {
  const wrapRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches)
      return;

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      setTilt({
        x: (e.clientX - r.left) / r.width - 0.5,
        y: (e.clientY - r.top) / r.height - 0.5,
      });
    };
    const onLeave = () => setTilt({ x: 0, y: 0 });
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  // Layered photo definitions — base position, rotation, z-order, mult
  const photos = [
    {
      key: "vanRear",
      img: IMAGES.team.vanRear,
      tag: "/ 02",
      label: "WEISSWAREN ABHOLUNG",
      pos: "top-0 left-2 md:left-6",
      size: "w-[60%] md:w-[55%] aspect-[5/4]",
      rotate: "-rotate-[8deg]",
      mult: 0.7,
      delay: "0.15s",
    },
    {
      key: "vanLoaded",
      img: IMAGES.team.vanLoaded,
      tag: "/ 03",
      label: "VOLL BELADEN",
      pos: "top-[26%] right-0",
      size: "w-[52%] md:w-[48%] aspect-[5/4]",
      rotate: "rotate-[6deg]",
      mult: 1.0,
      delay: "0.3s",
    },
    {
      key: "vanInAction",
      img: IMAGES.team.vanInAction,
      tag: "/ 04",
      label: "ENTRÜMPELUNG IN BREMEN",
      pos: "bottom-[8%] left-0 md:left-4",
      size: "w-[54%] md:w-[50%] aspect-[5/4]",
      rotate: "rotate-[4deg]",
      mult: 0.85,
      delay: "0.45s",
    },
    {
      key: "vanSide",
      img: IMAGES.team.vanSide,
      tag: "/ 01 · HAUPT",
      label: "BRS TRANSPORTER · 24/7",
      pos: "bottom-0 right-2 md:right-6",
      size: "w-[58%] md:w-[54%] aspect-[5/4]",
      rotate: "-rotate-[3deg]",
      mult: 1.25,
      delay: "0.6s",
      hero: true,
    },
  ];

  return (
    <div
      ref={wrapRef}
      data-testid="hero-photo-collage"
      className="relative w-full h-full"
      style={{ perspective: "1500px" }}
    >
      {/* Circular spinning text behind */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30">
        <svg viewBox="0 0 400 400" className="w-[360px] h-[360px] md:w-[460px] md:h-[460px] ring-spin">
          <defs>
            <path
              id="hero-ring2"
              d="M 200,200 m -160,0 a 160,160 0 1,1 320,0 a 160,160 0 1,1 -320,0"
            />
          </defs>
          <text
            fontFamily="JetBrains Mono, monospace"
            fontSize="12"
            fontWeight="700"
            letterSpacing="0.45em"
            fill="#0A0A0A"
          >
            <textPath href="#hero-ring2" startOffset="0">
              BREMER · RECYCLING · SERVICE · 24/7 · 0 € SCHROTT · BESENREIN ·
            </textPath>
          </text>
          <circle cx="200" cy="200" r="186" fill="none" stroke="#0A0A0A" strokeWidth="1" strokeDasharray="2 8" opacity="0.5" />
        </svg>
      </div>

      {photos.map((p, i) => {
        const tx = tilt.x * 18 * p.mult;
        const ty = tilt.y * 12 * p.mult;
        const rotY = tilt.x * 10 * p.mult;
        const rotX = -tilt.y * 8 * p.mult;
        return (
          <div
            key={p.key}
            className={`absolute ${p.pos} ${p.size} card-drop ${p.rotate}`}
            style={{
              animationDelay: p.delay,
              zIndex: 10 + i,
              transform: `translate(${tx}px, ${ty}px) perspective(1500px) rotateY(${rotY}deg) rotateX(${rotX}deg)`,
              transformStyle: "preserve-3d",
              transition: "transform 0.25s cubic-bezier(0.2,0.7,0.2,1)",
            }}
          >
            <div className="relative w-full h-full polaroid-frame float-soft">
              <img
                src={p.img}
                alt={p.label}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              {/* film gradient */}
              <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              {/* corner tag */}
              <div className="absolute top-2 md:top-3 left-2 md:left-3 font-mono text-[8px] md:text-[9px] tracking-[0.3em] uppercase text-white bg-black/55 backdrop-blur-sm border border-white/15 rounded-full px-2 py-0.5 md:px-2.5 md:py-1">
                {p.tag}
              </div>
              {/* hero badge */}
              {p.hero && (
                <div className="absolute top-2 md:top-3 right-2 md:right-3 font-mono text-[8px] md:text-[9px] tracking-[0.3em] uppercase text-black bg-[#25D366] rounded-full px-2 py-0.5 md:px-2.5 md:py-1 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-black blink-dot" />
                  LIVE
                </div>
              )}
              {/* label bottom */}
              <div className="absolute bottom-2 md:bottom-3 left-2 right-2 md:left-3 md:right-3">
                <div className="font-display text-[11px] md:text-sm font-medium text-white leading-tight">
                  {p.label}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Floating mini chip — 2x trucks */}
      <div
        className="absolute z-30 top-2 right-2 md:top-6 md:right-6 rise float-soft"
        style={{ animationDelay: "0.9s" }}
      >
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black text-white border border-black shadow-lg">
          <Truck className="w-3.5 h-3.5" />
          <span className="text-[11px] font-semibold tracking-tight">
            2 Transporter
          </span>
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   "JETZT FAHREN" BANNER — full width green band
========================================================= */
const NowDrivingBanner = () => (
  <a
    href={buildWhatsAppUrl(
      "Hallo BRS, können Sie jetzt direkt zu mir kommen? Hier ist meine Adresse: "
    )}
    target="_blank"
    rel="noreferrer"
    data-testid="now-driving-banner"
    className="group relative z-10 block mt-10 md:mt-14 mx-3 md:mx-8 lg:mx-12 rounded-[20px] md:rounded-[28px] bg-[#25D366] text-black overflow-hidden border border-black/10"
  >
    <div className="absolute -top-10 -left-6 w-40 h-40 rounded-full bg-black/8" />
    <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full border-2 border-black/15" />

    <div className="relative px-5 md:px-10 lg:px-14 py-5 md:py-7 flex items-center justify-between gap-4 md:gap-6">
      {/* truck icon shake */}
      <div className="flex items-center gap-3 md:gap-5 min-w-0">
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-black text-white flex items-center justify-center shrink-0">
          <Truck className="w-6 h-6 md:w-7 md:h-7 now-truck-shake" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-mono text-[9px] md:text-[10px] tracking-[0.35em] uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-black blink-dot" />
            HEUTE FREI · LIVE
          </div>
          <div className="font-display text-xl md:text-3xl lg:text-4xl font-semibold tracking-tight leading-[1.05] mt-0.5">
            Wir können jetzt zu Ihnen fahren!
          </div>
        </div>
      </div>
      <div className="hidden md:flex items-center gap-3 shrink-0">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">
          Adresse senden
        </span>
        <span className="w-11 h-11 rounded-full bg-black text-white flex items-center justify-center group-hover:translate-x-1 transition-transform">
          <ArrowRight className="w-5 h-5" />
        </span>
      </div>
      <span className="md:hidden w-9 h-9 rounded-full bg-black text-white flex items-center justify-center shrink-0">
        <ArrowRight className="w-4 h-4" />
      </span>
    </div>
  </a>
);

/* =========================================================
   ANIMATED TRUCK STRIP — SVG transporter driving across
========================================================= */
const TruckStrip = () => (
  <div className="relative mt-4 md:mt-6 h-16 md:h-24 overflow-hidden pointer-events-none">
    {/* horizon line / road */}
    <div className="absolute inset-x-4 md:inset-x-8 lg:inset-x-12 bottom-2 h-px bg-black/20" />
    <div className="absolute inset-x-4 md:inset-x-8 lg:inset-x-12 bottom-2 h-px">
      <div className="h-px w-full bg-[repeating-linear-gradient(90deg,#0A0A0A_0,#0A0A0A_14px,transparent_14px,transparent_28px)] opacity-30" />
    </div>

    {/* Driving truck */}
    <div className="absolute bottom-3 left-0 truck-drive">
      <BRSTruck />
    </div>
    {/* Driving truck #2 - opposite delay */}
    <div className="absolute bottom-3 left-0 truck-drive" style={{ animationDelay: "-7s", opacity: 0.5 }}>
      <SmallBox />
    </div>
  </div>
);

const BRSTruck = () => (
  <svg viewBox="0 0 220 90" className="w-32 h-12 md:w-52 md:h-20" aria-hidden>
    <defs>
      <linearGradient id="bodyGr" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="100%" stopColor="#E5E5E5" />
      </linearGradient>
    </defs>
    {/* body */}
    <rect x="10" y="20" width="140" height="50" rx="6" fill="url(#bodyGr)" stroke="#0A0A0A" strokeWidth="2" />
    {/* cabin */}
    <path d="M150 30 L180 30 L210 50 L210 70 L150 70 Z" fill="url(#bodyGr)" stroke="#0A0A0A" strokeWidth="2" />
    {/* windows */}
    <rect x="155" y="35" width="32" height="14" rx="2" fill="#0A0A0A" opacity="0.85" />
    {/* BRS logo on side */}
    <g transform="translate(40 30)">
      <rect x="0" y="0" width="30" height="30" rx="3" fill="#0A0A0A" />
      <text x="15" y="22" textAnchor="middle" fontFamily="Bricolage Grotesque" fontSize="14" fontWeight="700" fill="#FFFFFF">BRS</text>
    </g>
    <text x="85" y="42" fontFamily="Bricolage Grotesque" fontSize="11" fontWeight="700" fill="#0A0A0A" letterSpacing="0.5">
      BREMER RECYCLING
    </text>
    <text x="85" y="55" fontFamily="JetBrains Mono" fontSize="7" fontWeight="700" fill="#25D366" letterSpacing="2">
      24/7 · SCHROTT GRATIS
    </text>
    {/* wheels */}
    <circle cx="42" cy="73" r="9" fill="#0A0A0A" />
    <circle cx="42" cy="73" r="3" fill="#FFFFFF" />
    <circle cx="125" cy="73" r="9" fill="#0A0A0A" />
    <circle cx="125" cy="73" r="3" fill="#FFFFFF" />
    <circle cx="190" cy="73" r="9" fill="#0A0A0A" />
    <circle cx="190" cy="73" r="3" fill="#FFFFFF" />
    {/* headlight */}
    <circle cx="208" cy="52" r="2" fill="#25D366" />
  </svg>
);

const SmallBox = () => (
  <svg viewBox="0 0 60 60" className="w-12 h-12 md:w-16 md:h-16" aria-hidden>
    <rect x="6" y="14" width="48" height="36" fill="#FFFFFF" stroke="#0A0A0A" strokeWidth="2" />
    <line x1="6" y1="32" x2="54" y2="32" stroke="#0A0A0A" strokeWidth="1.5" strokeOpacity="0.4" />
    <line x1="30" y1="14" x2="30" y2="50" stroke="#0A0A0A" strokeWidth="1.5" strokeOpacity="0.4" />
    <text x="30" y="36" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="7" fontWeight="700" fill="#0A0A0A" opacity="0.6">FRAGILE</text>
  </svg>
);

/* =========================================================
   Background texture
========================================================= */
const BgTexture = () => (
  <>
    <div
      className="absolute inset-0 opacity-[0.04] pointer-events-none"
      aria-hidden
      style={{
        backgroundImage:
          "linear-gradient(rgba(10,10,10,1) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,10,1) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
        maskImage:
          "radial-gradient(ellipse at center top, black 30%, transparent 80%)",
      }}
    />
    <div
      className="absolute -top-24 right-[-10%] w-[600px] h-[600px] rounded-full pointer-events-none"
      style={{
        background:
          "radial-gradient(circle, rgba(37,211,102,0.10), transparent 65%)",
      }}
    />
    <div
      className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full pointer-events-none"
      style={{
        background:
          "radial-gradient(circle, rgba(10,10,10,0.05), transparent 65%)",
      }}
    />
  </>
);

const CountStat = ({ label, target, suffix = "", prefix = "" }) => {
  const [ref, inView] = useInView();
  const v = useCountUp(target, 1600, inView);
  return (
    <div ref={ref}>
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/45">
        {label}
      </div>
      <div className="mt-2 text-3xl md:text-5xl font-light tracking-[-0.02em] text-black tabular-nums font-display">
        {prefix}
        {v}
        {suffix}
      </div>
    </div>
  );
};
